import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Button, Group, Stack, Text } from '@repletoryreach/sdk-ui';
import { Alert, DataTable, EmptyState, StatusBadge, TableShell, type StatusTone } from '@repletoryreach/sdk-ui/data-display';
import { Tooltip } from '@repletoryreach/sdk-ui/overlays';
import { isApiError, settings } from '@repletoryreach/sdk-api';
import type { Indexed } from '../../../lib/types';
import { TwoLineCell } from '../../../components/TwoLineCell';
import { formatDateTime, formatInteger, formatRelative } from '../../../lib/format';
import { useT, type Translate } from '../../../i18n/I18nProvider';

const MUTED = 'var(--rr-color-text-muted)';

type AuditEvent = settings.OpsAuditEvent;

/**
 * `partial` is the outcome that matters: some messages moved and some were put
 * back. It is amber rather than green so a trail of them is visible at a
 * glance, and `pending` is amber too because a row stuck there means the
 * service stopped part-way through an action.
 */
const OUTCOME_TONE: Record<AuditEvent['outcome'], StatusTone> = {
  pending: 'pending',
  succeeded: 'healthy',
  partial: 'warning',
  failed: 'down',
  refused: 'inactive'
};

const OUTCOME_KEYS: Record<AuditEvent['outcome'], string> = {
  pending: 'queues.audit.outcome.pending',
  succeeded: 'queues.audit.outcome.succeeded',
  partial: 'queues.audit.outcome.partial',
  failed: 'queues.audit.outcome.failed',
  refused: 'queues.audit.outcome.refused'
};

const ACTION_KEYS: Record<string, string> = {
  'queue.requeue': 'queues.audit.action.requeue'
};

/**
 * Who did it, in words a person recognises.
 *
 * `actor` is a user's `sub`, which is a database join key and never belongs on
 * screen. The email is the name here; a `system:<service>` subject names the
 * service instead, and anything else is honestly unknown rather than a UUID.
 */
function actorLabel(t: Translate, event: AuditEvent): string {
  if (event.actor_email) return event.actor_email;
  if (event.actor.startsWith('system:')) return t('queues.audit.systemActor', { service: event.actor.slice(7) });
  return t('queues.audit.unknownActor');
}

function destinationLabel(t: Translate, event: AuditEvent): string {
  return event.destination ?? t('queues.audit.byFaultAddress');
}

/**
 * What this console has done to the broker, newest first.
 *
 * Every requeue is a move of customer messages, so the trail is on the page
 * itself rather than behind a link: the operator about to move more should be
 * able to see what was already moved, by whom and why. `source` narrows it to
 * one queue, which is how the detail drawer uses it.
 */
export function OpsAuditPanel({ source, limit = 25, compact = false }: {
  /** Narrow the trail to one queue by name. Omit for the whole platform. */
  source?: string;
  limit?: number;
  /** A short read-only list rather than the full table, for the detail drawer. */
  compact?: boolean;
}) {
  const t = useT();
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['admin', 'ops', 'audit', source ?? 'all', limit],
    queryFn: () => settings.opsAudit(source ? { source, limit } : { limit })
  });

  const rows = useMemo(() => (data?.data ?? []) as AuditEvent[], [data]);
  const total = data?.meta?.total ?? rows.length;
  const denied = isApiError(error) && error.status === 403;
  const failed = Boolean(error);

  if (compact) {
    return (
      <Stack gap="sm">
        <Text role="bodySmall" fw={600}>{t('queues.audit.drawerTitle')}</Text>
        {failed && (
          <Text role="caption" c="var(--rr-color-error)">
            {denied ? t('queues.audit.denied') : t('queues.audit.loadError')}
          </Text>
        )}
        {!failed && !isLoading && rows.length === 0 && (
          <Text role="caption" c={MUTED}>{t('queues.audit.emptyScoped', { queue: source ?? '' })}</Text>
        )}
        {rows.map((event) => (
          <Stack key={event.id} gap={0}>
            <Group justify="space-between" align="baseline" gap="sm" wrap="nowrap">
              <Text role="bodySmall" fw={600}>
                {t('queues.audit.movedOf', {
                  affected: formatInteger(event.affected_count),
                  requested: formatInteger(event.requested_count)
                })}
              </Text>
              <StatusBadge status={OUTCOME_TONE[event.outcome]} size="xs">
                {t(OUTCOME_KEYS[event.outcome])}
              </StatusBadge>
            </Group>
            <Text role="caption" c={MUTED} title={formatDateTime(event.started_at)}>
              {t('queues.audit.compactLine', {
                when: formatRelative(event.started_at),
                who: actorLabel(t, event),
                destination: destinationLabel(t, event)
              })}
            </Text>
            <Text role="caption" c={MUTED}>{event.reason}</Text>
          </Stack>
        ))}
      </Stack>
    );
  }

  return (
    <TableShell
      title={source ? t('queues.audit.titleScoped', { queue: source }) : t('queues.audit.title')}
      description={t('queues.audit.description')}
      totalLabel={t('queues.audit.totalLabel', { shown: rows.length, total })}
      actions={
        <Button size="sm" variant="default" onClick={() => refetch()} loading={isFetching}>{t('action.refresh')}</Button>
      }
      loading={isLoading}
      isEmpty={!isLoading && !failed && rows.length === 0}
      empty={
        <EmptyState
          title={source ? t('queues.audit.emptyScoped', { queue: source }) : t('queues.audit.empty')}
          description={t('queues.audit.emptyHint')}
        />
      }
      error={failed ? (
        <Alert color={denied ? 'warning' : 'error'}>
          <Group justify="space-between" align="center" style={{ width: '100%' }}>
            <span>{denied ? t('queues.audit.denied') : t('queues.audit.loadError')}</span>
            {!denied && <Button size="xs" variant="outline" onClick={() => refetch()}>{t('action.retry')}</Button>}
          </Group>
        </Alert>
      ) : null}
    >
      <DataTable<Indexed<AuditEvent>>
        records={rows as Indexed<AuditEvent>[]}
        loading={isLoading}
        idAccessor="id"
        emptyState={t('queues.audit.empty')}
        columns={[
          {
            accessor: 'started_at',
            title: t('queues.audit.columns.when'),
            render: (r) => (
              <TwoLineCell
                primary={formatRelative(r.started_at)}
                secondary={t(ACTION_KEYS[r.action] ?? 'queues.audit.action.other', { action: r.action })}
                title={formatDateTime(r.started_at)}
              />
            )
          },
          { accessor: 'actor', title: t('queues.audit.columns.who'), render: (r) => actorLabel(t, r) },
          {
            accessor: 'source',
            title: t('queues.audit.columns.queue'),
            render: (r) => <code>{r.source}</code>
          },
          {
            accessor: 'destination',
            title: t('queues.audit.columns.destination'),
            // A blank destination is not missing data: it is the routing rule
            // that each message went back to its own fault address.
            render: (r) => (r.destination
              ? <code>{r.destination}</code>
              : <Text role="bodySmall" c={MUTED}>{t('queues.audit.byFaultAddress')}</Text>)
          },
          {
            accessor: 'affected_count',
            title: t('queues.audit.columns.moved'),
            textAlign: 'right',
            render: (r) => (
              <span title={t('queues.audit.movedTooltip', { requested: formatInteger(r.requested_count) })}>
                {t('queues.audit.movedOf', {
                  affected: formatInteger(r.affected_count),
                  requested: formatInteger(r.requested_count)
                })}
              </span>
            )
          },
          {
            accessor: 'restored_count',
            title: t('queues.audit.columns.restored'),
            textAlign: 'right',
            // Amber only when it happened: a column of amber zeroes teaches
            // operators to ignore the colour.
            render: (r) => (
              <Text role="bodySmall" c={r.restored_count > 0 ? 'var(--rr-color-warning)' : undefined}>
                {formatInteger(r.restored_count)}
              </Text>
            )
          },
          {
            accessor: 'outcome',
            title: t('queues.audit.columns.outcome'),
            render: (r) => {
              const badge = (
                <StatusBadge status={OUTCOME_TONE[r.outcome]} size="xs">{t(OUTCOME_KEYS[r.outcome])}</StatusBadge>
              );
              if (r.failed_count > 0) {
                return (
                  <Tooltip label={t('queues.audit.failedTooltip', { count: formatInteger(r.failed_count) })} position="top">
                    {badge}
                  </Tooltip>
                );
              }
              return r.outcome === 'pending'
                ? <Tooltip label={t('queues.audit.pendingTooltip')} position="top">{badge}</Tooltip>
                : badge;
            }
          },
          {
            accessor: 'reason',
            title: t('queues.audit.columns.reason'),
            render: (r) => (
              <Text role="bodySmall" title={r.detail ?? r.reason} lineClamp={2}>{r.reason}</Text>
            )
          }
        ]}
      />
    </TableShell>
  );
}
